import { useCallback, useState } from "react";

/* The toast store drops a dismissed entry at once. To animate its exit the
   Toaster keeps it mounted a little longer: this mirrors the store into a
   render list where a vanished entry stays as `open: false` until its item
   reports the exit has finished (`onExited`). Order is kept, so the stack
   never reflows while one toast is leaving. */

export interface PresentToast<T> {
  readonly entry: T;
  readonly open: boolean;
}

function reconcile<T extends { id: number }>(
  items: readonly PresentToast<T>[],
  source: readonly T[],
): readonly PresentToast<T>[] {
  const live = new Map(source.map((entry) => [entry.id, entry]));
  const seen = new Set<number>();
  const next: PresentToast<T>[] = [];
  for (const item of items) {
    const entry = live.get(item.entry.id);
    seen.add(item.entry.id);
    next.push(
      entry ? { entry, open: true } : { entry: item.entry, open: false },
    );
  }
  for (const entry of source) {
    if (!seen.has(entry.id)) next.push({ entry, open: true });
  }
  return next;
}

export function useToastPresence<T extends { id: number }>(
  source: readonly T[],
): {
  readonly items: readonly PresentToast<T>[];
  readonly onExited: (id: number) => void;
} {
  const [state, setState] = useState(() => ({
    source,
    items: reconcile([], source),
  }));
  // A store change is a state-machine event: derive during render so a
  // dismissed toast is never painted as open (as useTransitionPresence does).
  if (state.source !== source) {
    setState({ source, items: reconcile(state.items, source) });
  }
  const onExited = useCallback((id: number) => {
    setState((current) => ({
      ...current,
      items: current.items.filter((item) => item.entry.id !== id),
    }));
  }, []);
  return { items: state.items, onExited };
}
